/**
 * ADR-0235 trust verdicts on the visits panel (founder 2026-10-13: "i dont
 * see a banner of trust anywhere!"). The SAME verdicts, matching and chip
 * as the Agents page (`useTrustVerdicts`, `matchAgentVerdict`,
 * `TrustChip`); this file only decides WHERE a chip may show, so a row
 * never carries two signals that contradict each other.
 *
 * Precedence (strongest first):
 *
 *   1. People: no chip. Verdicts are about agents.
 *   2. Mismatch (server `impersonation_reason`): no chip. The coral
 *      Mismatch mark already says this sender is not who it claims; the
 *      claimed agent's verdict (often "Verified") belongs to the real one.
 *   3. Probing (row or header): only a "Likely impersonator" verdict shows.
 *      A mint "Verified" next to the coral alert reads as a contradiction,
 *      and "Unverified" / "Suspicious" only repeat what the alert already
 *      says (founder: no clutter).
 *   4. List rows: no chip for a plain "Unverified" (no Suspicious, no
 *      Impersonated flag). The panel has no word for the default state
 *      (Step A); the detail header still shows it, with "Why?". With a
 *      flag, the row shows the flag alone ("Suspicious", compact chip).
 *
 * Everything else shows the verdict's chip. A Confirmed shield and a
 * "Verified" chip can sit together: both are positive (this visit was
 * proven; the name is verified over the window), never contradictory.
 * When verdicts are not live for the site, nothing renders at all.
 */

import { identityState } from '@sill/audit';
import type { AuditRecord } from '@sill/types';
import { inferUaProductToken, matchAgentVerdict, type AgentRowIdentity } from '@sill/types';
import type { MerchantAgentVerdict } from '@sill/types/api';

export type VisitTrustSurface = 'row' | 'header';

export interface VisitTrustContext {
  readonly surface: VisitTrustSurface;
  /** The row (or record) is a server-flagged Mismatch. */
  readonly mismatch: boolean;
  /** The row (or record) shows the Probing alert. */
  readonly probing: boolean;
}

/**
 * The identity `matchAgentVerdict` reads, built the way the server builds
 * claim keys (`claimKeyFor`): the registry id the SERVER matched, else
 * the UA product token.
 */
export function visitRowIdentity(record: AuditRecord): AgentRowIdentity {
  const id = record.identification_match?.agent_id ?? null;
  const token = inferUaProductToken((record.user_agent ?? '').slice(0, 512));
  return { agent_id: id !== null && id.length > 0 ? id : null, inferred_name: token };
}

/** The verdict whose chip this row / header shows, or `null` (see precedence above). */
export function visitTrustVerdict<T extends MerchantAgentVerdict>(
  record: AuditRecord,
  verdicts: readonly T[] | null,
  ctx: VisitTrustContext,
): T | null {
  if (verdicts === null || verdicts.length === 0) return null;
  if (record.identification_match?.visitor_class === 'human_likely') return null;
  if (ctx.mismatch || identityState(record).state === 'mismatch') return null;
  const v = matchAgentVerdict(visitRowIdentity(record), verdicts);
  if (v === null) return null;
  if (ctx.probing && v.label !== 'likely_impersonator') return null;
  if (ctx.surface === 'row' && v.label === 'unverified' && !v.suspicious && !v.impersonated) return null;
  return v;
}
